// Place-order block. Validates that a customer and at least one cart
// line are present, POSTs the order to the configured endpoint
// (default /proteus-vision-ecom-ext/sales-orders), writes the response
// into the bound output vars, AND persists the success result to the
// block's per-document captureState — so reopening the smart doc
// continues to show the success card without depending on transient
// page vars. Mirrors the transaction-embed captureState persistence
// pattern (see RuntimeBlockHost EmbedBody for the embed-side equivalent).

import { useMemo, useState } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { OrderSubmitBlock, OrderSubmitCaptureState } from '@/pages/designers/page/types'
import { apiPost, erpErrorMessage, type ErpOrderLine, type ErpOrderResult } from '../erpService'
import { readBinding, writeOutputs } from '../bindingResolver'
import { resolveEndpointUrl } from './endpoints'
import { injectEcomStyles } from './styles'

interface CartItem {
  productId: string
  styleId: string
  preferences?: Record<string, unknown>
  measurements?: Record<string, number>
  qty?: number
  unitPrice?: number
}

interface CustomerLike { id: string }

function fmtNum(n: number): string {
  try {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(n)
  } catch {
    return n.toFixed(2)
  }
}

export default function OrderSubmitRenderer({ block }: { block: OrderSubmitBlock }) {
  injectEcomStyles()
  const runtime = useRuntime()
  const ctx = useMemo(
    () => ({ vars: runtime.vars, inputs: runtime.page.inputs || [] }),
    [runtime.vars, runtime.page.inputs],
  )

  // Rehydrate from captureState (persisted via runtime.saveProperties).
  // Falls back to local state for non-document runs (designer canvas,
  // page preview).
  const persistedResult = block.captureState?.result
  const [result, setResult] = useState<OrderSubmitCaptureState['result'] | null>(
    persistedResult ? persistedResult : null,
  )
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const cartRaw = readBinding(block.cartBinding, ctx)
  const cart: CartItem[] = Array.isArray(cartRaw) ? (cartRaw as CartItem[]) : []
  const customerRaw = readBinding(block.customerBinding, ctx)
  const customer: CustomerLike | null =
    customerRaw && typeof customerRaw === 'object' && 'id' in (customerRaw as object)
      ? (customerRaw as CustomerLike) : null

  const itemCount = cart.length
  const totalAmount = cart.reduce(
    (sum, item) => sum + (Number(item.qty || 0)) * (Number(item.unitPrice || 0)),
    0,
  )
  const hasCustomer = !!customer
  const canSubmit = hasCustomer && itemCount > 0 && !submitting

  function resolveTitle(r: { orderNumber?: string }): string {
    const tmpl = block.successTitle || 'Order {{orderNumber}} created'
    const num = r.orderNumber || ''
    // Substitute {{orderNumber}}, {{salesOrderNumber}}, plus any var
    // names declared in outputBindings (so legacy templates keep working).
    let out = tmpl
      .replace(/\{\{\s*orderNumber\s*\}\}/g, num)
      .replace(/\{\{\s*salesOrderNumber\s*\}\}/g, num)
    for (const o of block.outputBindings || []) {
      if (o.target === 'orderNumber' && o.source.source === 'page_input' && o.source.name) {
        out = out.replace(new RegExp(`\\{\\{\\s*${o.source.name}\\s*\\}\\}`, 'g'), num)
      }
    }
    return out
  }

  function submit() {
    if (!canSubmit || !customer) return
    const url = resolveEndpointUrl(block.source)
    if (!url) {
      setErrorMsg('No endpoint configured. Set a URL in the block source.')
      return
    }
    const lines: ErpOrderLine[] = cart.map((item) => ({
      productId: item.productId,
      styleId: item.styleId,
      preferences: item.preferences || {},
      measurements: item.measurements || {},
      qty: Number(item.qty || 1),
      unitPrice: Number(item.unitPrice || 0),
    }))
    setSubmitting(true)
    setErrorMsg('')
    apiPost<ErpOrderResult>(url, { customerId: customer.id, lines })
      .then((r) => {
        // Build the persistable success result. Keep both `id`/`orderId`
        // and `orderNumber` so older templates keep substituting.
        const resultForState: OrderSubmitCaptureState['result'] = {
          orderId: r.id,
          orderNumber: r.orderNumber,
          status: r.status,
          totalAmount: r.totalAmount,
          currency: r.currency,
          createdAt: r.createdAt,
        }
        // Persist via the runtime's saveProperties bridge so a reopened
        // smart-doc rehydrates the success card from block.captureState.
        runtime.saveProperties(block.id, { captureState: { result: resultForState } })
        // Also write the configured output bindings — typically orderId
        // + orderNumber — into page vars so downstream blocks can react.
        writeOutputs(
          block.outputBindings,
          {
            value: r,
            orderId: r.id,
            orderNumber: r.orderNumber,
            status: r.status,
            totalAmount: r.totalAmount,
            currency: r.currency,
          },
          runtime.setVar,
        )
        setResult(resultForState)
        setSubmitting(false)
        if (block.onSuccessNavigateTo) {
          window.location.href = block.onSuccessNavigateTo
        }
      })
      .catch((err) => {
        setErrorMsg(erpErrorMessage(err, 'Failed to place order. Please try again.'))
        setSubmitting(false)
      })
  }

  function startNew() {
    // Clear the persisted success on the doc + reset local UI.
    runtime.saveProperties(block.id, { captureState: undefined })
    setResult(null)
    setErrorMsg('')
    // Clear bound vars: customer, cart, and any orderId/orderNumber outputs.
    if (block.customerBinding.source.source === 'page_input' && block.customerBinding.source.name) {
      runtime.setVar(block.customerBinding.source.name, null)
    }
    if (block.cartBinding.source.source === 'page_input' && block.cartBinding.source.name) {
      runtime.setVar(block.cartBinding.source.name, [])
    }
    for (const o of block.outputBindings || []) {
      if (o.source.source !== 'page_input' || !o.source.name) continue
      runtime.setVar(o.source.name, '')
    }
  }

  if (result) {
    return (
      <section className="osub osub--success">
        <div className="osub__icon">✓</div>
        <h3 className="osub__title">{resolveTitle(result)}</h3>
        {block.successBody ? (
          <p className="osub__body">{block.successBody}</p>
        ) : (
          <p className="osub__body">
            Recorded in Vision ERP{result.totalAmount !== undefined ? <> · Total <strong>{fmtNum(result.totalAmount)} {result.currency || ''}</strong></> : null}.
          </p>
        )}
        {block.showStartNew !== false && (
          <button className="osub__btn osub__btn--ghost" type="button" onClick={startNew}>
            Start new order
          </button>
        )}
      </section>
    )
  }

  return (
    <section className="osub">
      {errorMsg && <div className="osub__error">{errorMsg}</div>}
      <div className="osub__row">
        <div className="osub__summary">
          <div className="osub__items">
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </div>
          {totalAmount > 0 && (
            <div className="osub__total">Total <strong>{fmtNum(totalAmount)}</strong></div>
          )}
        </div>
        <button
          className="osub__btn osub__btn--primary"
          type="button"
          onClick={submit}
          disabled={!canSubmit}
        >
          {submitting ? 'Placing…' : (block.buttonLabel || 'Place Order')}
        </button>
      </div>
      {!canSubmit && !submitting && (
        <div className="osub__hint">
          {!hasCustomer
            ? 'Pick a customer to enable order placement.'
            : itemCount === 0 ? 'Add at least one item to the cart.' : ''}
        </div>
      )}
    </section>
  )
}
